/**
 * Image-area tool.
 *
 * Draws a polygon (shared drawing core in `polygonSelectionTool.ts`), fetches
 * the Feltkatalog images inside it via `FileManager/List` (`area` filter) and
 * renders them as clustered camera-pin billboards in
 * `smartdataImageDataSource`. Clicks:
 *
 *  - single billboard → sidebar (IMAGE_AREA) with that image + auto-open lightbox
 *  - cluster billboard → sidebar listing exactly the clustered images
 *    (clusters never reach `selectedEntityChanged`, so a dedicated
 *    ScreenSpaceEventHandler resolves them via the pick id set in
 *    `configureClusters`)
 *  - polygon → selected state (highlight) + sidebar listing ALL images
 *
 * The polygon entity lives in the same datasource as the billboards, so it
 * passes the pick whitelist and cleanup is a single `removeAll()`.
 */

import {
	Cartesian2,
	Cartesian3,
	Color,
	ColorMaterialProperty,
	HeadingPitchRange,
	HeightReference,
	Math as CesiumMath,
	PolygonHierarchy,
	ScreenSpaceEventHandler,
	ScreenSpaceEventType,
	VerticalOrigin,
	type Entity,
	type Viewer
} from 'cesium';
import { get } from 'svelte/store';
import { v4 } from 'uuid';

import { useCesiumViewer } from '../cesium-viewer';
import { useDataSources } from '../cesium-datasources';
import { setSelectedEntity } from '../map-utils';
import { activatePolygonSelectionTool } from './polygonSelectionTool';
import { imageAreaState, IMAGE_AREA_BILLBOARD_CAP } from './image-area-state.svelte';
import { fetchImageList } from '$lib/data/queries';
import { floatingSidemenuState } from '$lib/stores/floating-sidemenu-store';
import { SideMenuOptions, ENTITY_ID_PREFIX } from '$lib/utils/constants';
import { toast } from '$lib/components/organisms/toast/toast';
import type { IProjectModel } from '$lib/models/IProjectModel';
import type { ISmartData } from '$lib/models/ISmartData';

// ── Visuals ──────────────────────────────────────────────────────────────────

/** Brand primary — distinct from area-mode's cyan polygon. */
const AREA_COLOR = Color.fromCssColorString('#594EF2');
const FILL_ALPHA_DEFAULT = 0.15;
const FILL_ALPHA_SELECTED = 0.35;

// Teardrop pin with a camera glyph. One shared data URL → one texture for all
// billboards regardless of image count.
const CAMERA_PIN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="30" height="45"><path fill="#594EF2" stroke="white" stroke-width="1.5" d="M12 1C6.5 1 2 5.5 2 11c0 7 10 23 10 23s10-16 10-23C22 5.5 17.5 1 12 1z"/><rect x="9.9" y="6.6" width="4.2" height="2" rx="0.8" fill="white"/><rect x="6" y="7.8" width="12" height="8" rx="1.3" fill="white"/><circle cx="12" cy="11.8" r="2.3" fill="#594EF2"/></svg>`;
const CAMERA_PIN_DATA_URL = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(CAMERA_PIN_SVG)}`;

// ── Module-local state ───────────────────────────────────────────────────────

let clusterClickHandler: ScreenSpaceEventHandler | null = null;

// ── Public API ───────────────────────────────────────────────────────────────

/** Starts drawing a new image area. Any existing area is removed first. */
export function activateImageAreaTool(): void {
	removeImageArea();
	activatePolygonSelectionTool({
		toolKey: 'imageAreaSelection',
		onComplete: onPolygonComplete
	});
}

/** Removes the polygon + billboards and resets all image-area state. */
export function removeImageArea(): void {
	destroyClusterClickHandler();
	clearLocateMarker();
	const { smartdataImageDataSource } = useDataSources();
	smartdataImageDataSource.entities.removeAll();

	const viewer = useCesiumViewer();
	if (
		viewer?.selectedEntity &&
		(viewer.selectedEntity.id.startsWith(ENTITY_ID_PREFIX.IMAGE_AREA) ||
			viewer.selectedEntity.id.startsWith(ENTITY_ID_PREFIX.SMARTDATA_IMAGE))
	) {
		setSelectedEntity(undefined);
	}
	if (get(floatingSidemenuState) === SideMenuOptions.IMAGE_AREA) {
		floatingSidemenuState.set(SideMenuOptions.NONE);
	}
	imageAreaState.reset();
}

/**
 * Selection-pipeline hook (called from `selected-entity-behaviour.ts`) for the
 * tool's own entities. Returns without side effects for anything else.
 */
export function handleImageAreaEntitySelected(entity: Entity): void {
	if (entity.id.startsWith(ENTITY_ID_PREFIX.SMARTDATA_IMAGE)) {
		const imageId = Number(entity.id.slice(ENTITY_ID_PREFIX.SMARTDATA_IMAGE.length));
		if (!Number.isFinite(imageId)) return;
		// Single billboard: sidebar shows just this image and its lightbox opens.
		imageAreaState.showCluster([imageId], imageId);
		floatingSidemenuState.set(SideMenuOptions.IMAGE_AREA);
		return;
	}
	if (entity.id.startsWith(ENTITY_ID_PREFIX.IMAGE_AREA)) {
		setPolygonHighlight(true);
		imageAreaState.showAll();
		floatingSidemenuState.set(SideMenuOptions.IMAGE_AREA);
	}
}

/** Deselection hook — clears the polygon highlight when it loses selection. */
export function handleImageAreaEntityDeselected(previous: Entity | null | undefined): void {
	if (previous?.id?.startsWith(ENTITY_ID_PREFIX.IMAGE_AREA)) {
		setPolygonHighlight(false);
	}
}

/**
 * Flies the camera to an image's position ("vis i kartet" from the sidebar).
 * Prefers the billboard entity (nice framing via `viewer.flyTo`); images beyond
 * the billboard cap fall back to their raw coordinates. A label pinpoints the exact
 * image — neighbouring billboards/clusters make the camera move alone too ambiguous,
 * and a clustered image has no visible pin of its own at all.
 */
export function flyToImage(image: ISmartData.ImageItem): void {
	const viewer = useCesiumViewer();
	if (!viewer) return;
	const { smartdataImageDataSource } = useDataSources();
	const entity = smartdataImageDataSource.entities.getById(
		`${ENTITY_ID_PREFIX.SMARTDATA_IMAGE}${image.id}`
	);
	showLocateMarker(viewer, image);
	if (entity) {
		void viewer.flyTo(entity, {
			duration: 1.2,
			offset: new HeadingPitchRange(0, CesiumMath.toRadians(-45), 250)
		});
		return;
	}
	const c = image.coordinates;
	if (c?.latitude != null && c?.longitude != null) {
		viewer.camera.flyTo({
			destination: Cartesian3.fromDegrees(c.longitude, c.latitude, 400),
			duration: 1.2
		});
	}
}

// ── Internal: locate marker ──────────────────────────────────────────────────
// Lives in `temp_referenceDataSource` (same as MapCustomSearch's address
// marker): entities there are deliberately click-inert in the selection
// pipeline, so the marker never swallows clicks meant for billboards beneath.

const LOCATE_MARKER_ID = 'imageLocateMarker';
const LOCATE_MARKER_LIFETIME_MS = 6000;
let locateMarkerTimeout: ReturnType<typeof setTimeout> | null = null;

function clearLocateMarker(): void {
	if (locateMarkerTimeout) {
		clearTimeout(locateMarkerTimeout);
		locateMarkerTimeout = null;
	}
	const { temp_referenceDataSource } = useDataSources();
	const existing = temp_referenceDataSource.entities.getById(LOCATE_MARKER_ID);
	if (existing) temp_referenceDataSource.entities.remove(existing);
}

function showLocateMarker(viewer: Viewer, image: ISmartData.ImageItem): void {
	clearLocateMarker();
	const c = image.coordinates;
	if (!c || c.latitude == null || c.longitude == null) return;

	const { temp_referenceDataSource } = useDataSources();
	if (!viewer.dataSources.contains(temp_referenceDataSource)) {
		void viewer.dataSources.add(temp_referenceDataSource);
	}

	// Label only — a point marker just hides beneath the billboard/cluster.
	// Offset clears the 45px pin (and the CENTER-anchored cluster pill), so the
	// name floats right above whichever marker the image belongs to.
	temp_referenceDataSource.entities.add({
		id: LOCATE_MARKER_ID,
		position: Cartesian3.fromDegrees(c.longitude, c.latitude),
		label: {
			text: image.displayName || image.originalName || `Bilde ${image.id}`,
			font: '13px sans-serif',
			fillColor: Color.WHITE,
			showBackground: true,
			backgroundColor: Color.fromCssColorString('#0C003E').withAlpha(0.85),
			backgroundPadding: new Cartesian2(8, 5),
			pixelOffset: new Cartesian2(0, -52),
			verticalOrigin: VerticalOrigin.BOTTOM,
			heightReference: HeightReference.CLAMP_TO_GROUND,
			disableDepthTestDistance: Number.POSITIVE_INFINITY
		}
	});
	locateMarkerTimeout = setTimeout(clearLocateMarker, LOCATE_MARKER_LIFETIME_MS);
}

// ── Internal: polygon completion ─────────────────────────────────────────────

function onPolygonComplete({
	viewer,
	vertices,
	ring
}: {
	viewer: Viewer;
	vertices: Cartesian3[];
	polygonLngLat: [number, number][];
	ring: [number, number][];
}): void {
	const { smartdataImageDataSource } = useDataSources();
	const polygon: IProjectModel.Area = { type: 'Polygon', coordinates: [ring] };

	const polygonEntity = smartdataImageDataSource.entities.add({
		id: `${ENTITY_ID_PREFIX.IMAGE_AREA}${v4()}`,
		polygon: {
			hierarchy: new PolygonHierarchy(vertices),
			material: new ColorMaterialProperty(AREA_COLOR.withAlpha(FILL_ALPHA_DEFAULT)),
			outline: true,
			outlineColor: AREA_COLOR,
			outlineWidth: 2,
			heightReference: HeightReference.CLAMP_TO_GROUND
		},
		properties: { entityType: 'IMAGE_AREA_POLYGON', data: {} }
	});

	imageAreaState.active = true;
	imageAreaState.polygon = polygon;
	imageAreaState.polygonEntity = polygonEntity;
	imageAreaState.showAll();

	installClusterClickHandler(viewer);
	void loadImages(polygon);

	// Select the polygon so it highlights and the sidebar opens through the
	// normal selection pipeline. Deferred: the drawing tool flips its
	// `toolEnabled` flag right after this callback, and the selection callback
	// ignores selections made while a tool is still enabled.
	setTimeout(() => setSelectedEntity(polygonEntity), 0);
}

async function loadImages(polygon: IProjectModel.Area): Promise<void> {
	imageAreaState.loading = true;
	imageAreaState.error = null;
	try {
		const result = await fetchImageList({
			area: polygon,
			search: null,
			capturedFrom: null,
			capturedTo: null,
			hasLocation: true,
			folder: null,
			includeSubfolders: false,
			sortColumn: 'CapturedAt',
			sortAscending: false,
			page: 1,
			pageSize: IMAGE_AREA_BILLBOARD_CAP
		});
		imageAreaState.images = result.items ?? [];
		imageAreaState.totalCount = result.totalCount ?? 0;
		buildBillboards(imageAreaState.images);
	} catch (error) {
		console.error(error);
		// The sidebar's own (paginated) query may still succeed, so images can
		// show in the list even when this billboard fetch fails — keep the
		// message general: something MAY be missing, not everything failed.
		imageAreaState.error = 'Noe gikk galt — enkelte bilder vises kanskje ikke.';
		toast({
			type: 'warning',
			title: 'Kunne ikke hente alle bilder',
			description:
				'Noe gikk galt ved henting av bilder i området. Enkelte bilder vises kanskje ikke i kartet eller listen.'
		});
	} finally {
		imageAreaState.loading = false;
	}
}

// ── Internal: billboards ─────────────────────────────────────────────────────

function buildBillboards(images: ISmartData.ImageItem[]): void {
	const { smartdataImageDataSource } = useDataSources();

	// Keep the polygon; replace any previous billboards.
	smartdataImageDataSource.entities.values
		.filter((entity) => entity.id.startsWith(ENTITY_ID_PREFIX.SMARTDATA_IMAGE))
		.forEach((entity) => smartdataImageDataSource.entities.remove(entity));

	for (const image of images) {
		const c = image.coordinates;
		if (!c || c.latitude == null || c.longitude == null) continue;
		smartdataImageDataSource.entities.add({
			id: `${ENTITY_ID_PREFIX.SMARTDATA_IMAGE}${image.id}`,
			position: Cartesian3.fromDegrees(c.longitude, c.latitude),
			billboard: {
				image: CAMERA_PIN_DATA_URL,
				verticalOrigin: VerticalOrigin.BOTTOM,
				heightReference: HeightReference.CLAMP_TO_GROUND,
				disableDepthTestDistance: Number.POSITIVE_INFINITY
			},
			properties: { entityType: 'SMARTDATA_IMAGE', data: image }
		});
	}
}

// ── Internal: cluster clicks ─────────────────────────────────────────────────

// Cluster billboards are primitives, not entities — `selectedEntityChanged`
// never fires for them. `configureClusters` (type 'images') sets the cluster
// billboard's pick id to the clustered entities, which this handler resolves.
function installClusterClickHandler(viewer: Viewer): void {
	destroyClusterClickHandler();
	clusterClickHandler = new ScreenSpaceEventHandler(viewer.scene.canvas as HTMLCanvasElement);
	clusterClickHandler.setInputAction((event: { position: Cartesian2 }) => {
		const picked = viewer.scene.pick(event.position);
		const pickedId: unknown = picked?.id;
		if (!Array.isArray(pickedId)) return; // single entities go through selectedEntityChanged
		const imageIds = (pickedId as Entity[])
			.filter(
				(entity) =>
					typeof entity.id === 'string' &&
					entity.id.startsWith(ENTITY_ID_PREFIX.SMARTDATA_IMAGE)
			)
			.map((entity) => Number(entity.id.slice(ENTITY_ID_PREFIX.SMARTDATA_IMAGE.length)))
			.filter((id) => Number.isFinite(id));
		if (!imageIds.length) return;
		imageAreaState.showCluster(imageIds);
		floatingSidemenuState.set(SideMenuOptions.IMAGE_AREA);
	}, ScreenSpaceEventType.LEFT_CLICK);
}

function destroyClusterClickHandler(): void {
	clusterClickHandler?.destroy();
	clusterClickHandler = null;
}

// ── Internal: highlight ──────────────────────────────────────────────────────

function setPolygonHighlight(selected: boolean): void {
	imageAreaState.polygonSelected = selected;
	const entity = imageAreaState.polygonEntity;
	if (!entity?.polygon) return;
	entity.polygon.material = new ColorMaterialProperty(
		AREA_COLOR.withAlpha(selected ? FILL_ALPHA_SELECTED : FILL_ALPHA_DEFAULT)
	);
}
